import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  invalid?: boolean;
  selectSize?: 'sm' | 'lg';
  options?: SelectOption[];
  children?: ReactNode;
}

/** Native Bootstrap 5 form-select with label, sizing, and validation feedback. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, invalid = false, selectSize, options, children, id, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const classes = [
    'form-select',
    selectSize ? `form-select-${selectSize}` : '',
    invalid ? 'is-invalid' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mb-3">
      {label && (
        <label className="form-label" htmlFor={selectId}>
          {label}
        </label>
      )}
      <select id={selectId} ref={ref} className={classes} {...rest}>
        {options
          ? options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          : children}
      </select>
      {hint && <div className={invalid ? 'invalid-feedback d-block' : 'form-text'}>{hint}</div>}
    </div>
  );
});
